import { useCallback, useEffect, useRef, useState } from "react";
import { apiRequest, ApiError } from "src/services/apiClient";
import { notifyRecipientDataStale } from "src/services/recipientDataRefresh";
import type { Recipient } from "src/types";

const POLL_MS = 15_000;
// Consent can change without anything happening in this browser — a colleague records it, or a
// patient scans an on-site QR (that link has no sentAt, so the row never reads "sent"). A slow
// background poll keeps every team member's list current, not just the one who acted.
const IDLE_POLL_MS = 60_000;

/**
 * Patients list with live reload: fast while any questionnaire is awaiting submission, slow
 * otherwise. Only while the tab is visible.
 */
export function useLiveRecipientsList() {
    const [recipients, setRecipients] = useState<Recipient[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const load = useCallback(async () => {
        try {
            notifyRecipientDataStale();
            const res = await apiRequest<Recipient[]>("/recipients", {
                method: "GET",
                cache: "no-store",
            });
            setRecipients(Array.isArray(res) ? res.map((r) => ({ ...r })) : []);
            setError(null);
        } catch (e) {
            setError(e instanceof ApiError ? e.message : "Failed to load data.");
        } finally {
            setLoading(false);
        }
    }, []);

    const stopPoll = useCallback(() => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    }, []);

    useEffect(() => {
        setLoading(true);
        void load();
        return stopPoll;
    }, [load, stopPoll]);

    const hasAwaiting = recipients.some(
        (r) => r.questionnaireStatus === "sent" || r.questionnaireStatus === "opened",
    );

    useEffect(() => {
        stopPoll();
        pollRef.current = setInterval(() => {
            if (document.visibilityState === "visible") void load();
        }, hasAwaiting ? POLL_MS : IDLE_POLL_MS);
        return stopPoll;
    }, [hasAwaiting, load, stopPoll]);

    useEffect(() => {
        const wake = () => {
            if (document.visibilityState === "visible") void load();
        };
        window.addEventListener("focus", wake);
        document.addEventListener("visibilitychange", wake);
        return () => {
            window.removeEventListener("focus", wake);
            document.removeEventListener("visibilitychange", wake);
        };
    }, [load]);

    return { recipients, loading, error, reload: load };
}
